import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import type { FlightCartIntent, FlightCartLegMetadata, FlightCartReactivationMode } from 'tripPlanner/api/flightCart'
import {
  isCartableFlightSavedItemData,
  isChatFlightSavedItemData,
  isLeFlightSavedItemData,
  type CartableFlightSavedItemData,
  type LeFlightSavedItemData,
  type TransportConnection,
  type TransportOption,
} from 'tripPlanner/api/types'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { getFlightsTotal } from 'lib/flights/flightUtils'
import { isBookedFlightOption, parseSearchParamsCurrency } from '../components/ShortlistedFlightsSection/flightShortlistUtils'

export type FlightCartPanelStatus =
  | 'active'
  | 'needs_flight_offer'
  | 'needs_flight_selection'
  | 'pending_hydration'

export interface ExtractedFlightCartPanelOption {
  productType: 'flight'
  tripItemId: number
  returnTripItemId: number | undefined
  pairedTripItemIds: Array<number>
  option: TransportOption
  returnOption: TransportOption | undefined
  savedItemData: CartableFlightSavedItemData
  returnSavedItemData: LeFlightSavedItemData | undefined
  cartStatus: FlightCartPanelStatus
  cartExcluded: boolean
  cartItemId: string | undefined
  cartItem: App.MultiCartFlightItem | undefined
  savedPrice: number | undefined
  savedCurrencyCode: string | undefined
  cartPrice: number | undefined
  cartCurrencyCode: string | undefined
  includeInTotal: boolean
  isAvailabilityBlocked: boolean
}

export interface FlightCartIntentInput {
  savedItemData: CartableFlightSavedItemData
  hasActiveCartItem: boolean
  isRefresh: boolean
  selectedJourneyChanged: boolean
}

export interface FlightCartAvailabilityInput {
  cartExcluded: boolean
  isAvailabilityBlocked: boolean
}

export interface FlightLegMetadataInput {
  journey: App.JourneyV2
  savedItemData?: LeFlightSavedItemData
  searchId: string
  searchExpiry: number
  searchParams?: string
}

export interface FlightSavedPriceAndCurrency {
  price: number | undefined
  currencyCode: string | undefined
}

function getFlightCartItem(
  cartItemId: string | undefined,
  cartItemsById: Map<string, App.MultiCartItem>,
): App.MultiCartFlightItem | undefined {
  if (!cartItemId) return undefined
  const cartItem = cartItemsById.get(cartItemId)
  if (!cartItem || !isMultiCartFlightItem(cartItem)) return undefined
  if (!isFlightItem(cartItem.item)) return undefined
  return cartItem
}

export function getFlightCartItemId(option: TransportOption): string | undefined {
  return option.cartItemId ?? option.returnLeg?.cartItemId
}

export function getPairedFlightTripItemIds(option: TransportOption): Array<number> {
  return option.returnLeg ? [option.itemId, option.returnLeg.itemId] : [option.itemId]
}

function resolveFlightSavedPriceAndCurrency(
  savedItemData: CartableFlightSavedItemData,
  isReturn: boolean,
): FlightSavedPriceAndCurrency {
  if (isChatFlightSavedItemData(savedItemData)) {
    return { price: undefined, currencyCode: undefined }
  }

  const price = isReturn ? savedItemData.farePrice : savedItemData.price
  const currencyCode =
    savedItemData.currencyCode ??
    savedItemData.fareCurrencyCode ??
    parseSearchParamsCurrency(savedItemData.searchParams)

  return { price, currencyCode }
}

function resolveFlightCartPrice(cartItem: App.MultiCartFlightItem | undefined): number | undefined {
  if (!cartItem || !isFlightItem(cartItem.item)) return undefined
  return getFlightsTotal(cartItem.item)
}

export function isFlightSearchExpired(searchExpiry: number | undefined, now: number = Date.now()): boolean {
  return typeof searchExpiry === 'number' && searchExpiry <= now
}

export function isActiveFlightAvailabilityBlocked(
  cartItem: App.MultiCartFlightItem | undefined,
  now: number = Date.now(),
): boolean {
  if (!cartItem) return false
  if (cartItem.status === 'unavailable' || cartItem.status === 'dates_in_past') return true
  if (!isFlightItem(cartItem.item)) return false
  return isFlightSearchExpired(cartItem.item.expiry, now)
}

export function resolveFlightCartPanelStatus(
  option: TransportOption,
  cartItem: App.MultiCartFlightItem | undefined,
): FlightCartPanelStatus {
  const savedItemData = option.savedItemData
  if (isChatFlightSavedItemData(savedItemData)) return 'needs_flight_offer'
  if (!isLeFlightSavedItemData(savedItemData)) return 'pending_hydration'
  if (option.cartStatus === 'active' && cartItem) return 'active'
  if (option.cartStatus === 'active' && !cartItem) return 'needs_flight_selection'
  if (option.cartStatus === 'needs_flight_selection') return 'needs_flight_selection'
  if (!getFlightCartItemId(option)) return 'needs_flight_selection'
  return 'pending_hydration'
}

export function resolveFlightCartIntent({
  savedItemData,
  hasActiveCartItem,
  isRefresh,
  selectedJourneyChanged,
}: FlightCartIntentInput): FlightCartIntent {
  if (isChatFlightSavedItemData(savedItemData)) return 'convert_and_sync'
  if (isRefresh) return 'refresh'
  if (hasActiveCartItem || selectedJourneyChanged) return 'replace'
  return 'sync'
}

export function resolveFlightCartReactivationMode({
  cartExcluded,
  isAvailabilityBlocked,
}: FlightCartAvailabilityInput): FlightCartReactivationMode {
  return isAvailabilityBlocked && !cartExcluded ? 'availability_recovery' : 'preserve_exclusion'
}

export function buildFlightCartLegMetadata({
  journey,
  savedItemData,
  searchId,
  searchExpiry,
  searchParams,
}: FlightLegMetadataInput): FlightCartLegMetadata | undefined {
  const firstFlight = journey.flightGroup.flights[0]
  const lastFlight = journey.flightGroup.flights[journey.flightGroup.flights.length - 1]
  if (!firstFlight || !lastFlight) return undefined

  return {
    source: 'le_flight',
    flightGroupId: savedItemData?.flightGroupId ?? journey.flightGroup.id,
    legOrder: savedItemData?.legOrder,
    departureAirport: firstFlight.departureAirport,
    arrivalAirport: lastFlight.arrivalAirport,
    departureTime: firstFlight.departureDateTime,
    arrivalTime: lastFlight.arrivalDateTime,
    airline: journey.carrierName,
    flightNumber: firstFlight.flightNumber,
    carrierLogo: journey.carrierLogo,
    farePrice: journey.price.adult.totalFare,
    fareCurrencyCode: journey.currencyCode,
    price: journey.price.adult.totalFare,
    currencyCode: journey.currencyCode,
    searchId,
    journeyKey: journey.bookingInfo.journeyKey,
    journeyId: journey.id,
    searchExpiry,
    searchParams: searchParams ?? savedItemData?.searchParams,
  }
}

export function extractFlightCartPanelOptions(
  transports: Array<TransportConnection> | undefined,
  cartItemsById: Map<string, App.MultiCartItem>,
  now: number = Date.now(),
): Array<ExtractedFlightCartPanelOption> {
  if (!transports) return []

  const results: Array<ExtractedFlightCartPanelOption> = []
  const seenItemIds = new Set<number>()

  for (const transport of transports) {
    for (const option of transport.options) {
      if (seenItemIds.has(option.itemId)) continue
      if (isBookedFlightOption(option)) continue
      const savedItemData = option.savedItemData
      if (!isCartableFlightSavedItemData(savedItemData)) continue

      const returnOption = option.returnLeg
      const returnSavedItemData = returnOption?.savedItemData
      const cartItemId = getFlightCartItemId(option)
      const cartItem = getFlightCartItem(cartItemId, cartItemsById)
      const cartStatus = resolveFlightCartPanelStatus(option, cartItem)
      const pairedTripItemIds = getPairedFlightTripItemIds(option)
      const isReturn = !!returnOption
      const savedPrice = resolveFlightSavedPriceAndCurrency(savedItemData, isReturn)
      const cartPrice = resolveFlightCartPrice(cartItem)
      const isAvailabilityBlocked = isActiveFlightAvailabilityBlocked(cartItem, now)

      for (const itemId of pairedTripItemIds) {
        seenItemIds.add(itemId)
      }

      results.push({
        productType: 'flight',
        tripItemId: option.itemId,
        returnTripItemId: returnOption?.itemId,
        pairedTripItemIds,
        option,
        returnOption,
        savedItemData,
        returnSavedItemData: isLeFlightSavedItemData(returnSavedItemData) ? returnSavedItemData : undefined,
        cartStatus,
        cartExcluded: option.cartExcluded ?? returnOption?.cartExcluded ?? false,
        cartItemId,
        cartItem,
        savedPrice: savedPrice.price,
        savedCurrencyCode: savedPrice.currencyCode,
        cartPrice,
        cartCurrencyCode: savedPrice.currencyCode,
        includeInTotal: cartStatus !== 'active' || !isAvailabilityBlocked,
        isAvailabilityBlocked,
      })
    }
  }

  return results
}
